import { useMemo } from 'react';

import { NormalizedRebalancingPlan } from '@cast/types';

import {
  useKentRebalancePlanQuery,
  useRebalancePlanQuery,
} from 'hooks/queries/rebalancer';

import { useIsKentRebalancingMode } from './useIsKentRebalancingMode';
import {
  mapAutoscalerRebalancingPlanToNormalizedRebalancingPlan,
  mapKentRebalancingPlanToNormalizedRebalancingPlan,
} from '../utils/normalizedRebalancingPlan';

type UseCorrectRebalancePlanQueryProps = {
  clusterId?: string;
  rebalancePlanId?: string;
  enabled?: boolean;
  /** Autoscaler only — Kent always returns these */
  includeOperations?: boolean;
  /** Autoscaler only — Kent always returns these */
  includeConfigurations?: boolean;
  /** Autoscaler only — Kent does not support tour tags */
  tag?: string | number;
  meta?: Record<string, unknown>;
  refetchInterval?: number | false;
};

type UseCorrectRebalancePlanQuery = {
  isSuccess: boolean;
  isLoading: boolean;
  isError: boolean;
  normalizedRebalancingPlan?: NormalizedRebalancingPlan;
  error?: unknown;
  refetch: () => void;
};

export const useCorrectRebalancePlanQuery = ({
  enabled = true,
  ...rest
}: UseCorrectRebalancePlanQueryProps): UseCorrectRebalancePlanQuery => {
  const shouldUseKentApi = useIsKentRebalancingMode();

  const regularResponse = useRebalancePlanQuery({
    ...rest,
    enabled: enabled && !shouldUseKentApi,
  });

  const kentResponse = useKentRebalancePlanQuery({
    ...rest,
    enabled: enabled && shouldUseKentApi,
  });

  const normalizedRebalancingPlan = useMemo<
    NormalizedRebalancingPlan | undefined
  >(() => {
    if (shouldUseKentApi) {
      return kentResponse.rebalancePlan
        ? mapKentRebalancingPlanToNormalizedRebalancingPlan(
            kentResponse.rebalancePlan
          )
        : undefined;
    }

    return regularResponse.rebalancePlan
      ? mapAutoscalerRebalancingPlanToNormalizedRebalancingPlan(
          regularResponse.rebalancePlan
        )
      : undefined;
  }, [
    shouldUseKentApi,
    kentResponse.rebalancePlan,
    regularResponse.rebalancePlan,
  ]);

  return shouldUseKentApi
    ? { ...kentResponse, normalizedRebalancingPlan }
    : { ...regularResponse, normalizedRebalancingPlan };
};
